import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Rectangle,
  Polygon,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import {
  getReadingsForMap,
  getMapClusters,
  getEmployees,
  getTarifas,
  updateReading,
  createRoute,
  getRoutes,
  deleteRoute,
  updateRoute,
  getRouteHistory,
  completeRouteAssignment,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  Search,
  Edit2,
  Save,
  X,
  RefreshCw,
  Layers,
  MapPin,
  Info,
  Navigation2,
  Trash2,
  CheckCircle2,
  MousePointerClick,
  RectangleHorizontal,
  User,
  Clock,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

// ── Canvas renderers ──────────────────────────────────────────────────────────
const CANVAS_RENDERER = L.canvas({ padding: 0.5 });
const SELECTED_CANVAS = L.canvas({ padding: 0.5 });
const DEBOUNCE_MS = 350;

const ROUTE_COLORS = [
  "#D4620D",
  "#16a34a",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#b45309",
];

// Replica exacta de la fórmula de grid del backend
function getGridForZoom(zoom) {
  const z = parseInt(zoom);
  if (z < 9)  return 0.5;
  if (z < 11) return 0.1;
  if (z < 13) return 0.02;
  if (z < 15) return 0.005;
  if (z < 17) return 0.001;
  if (z < 18) return 0.0003;
  if (z < 19) return 0.0001;
  return 0.00001;
}

function exactClusterBbox(lat, lng, zoom) {
  const grid = getGridForZoom(zoom);
  const cellLat = Math.round(lat / grid) * grid;
  const cellLng = Math.round(lng / grid) * grid;
  const eps = 1e-9;
  return {
    minLat: cellLat - grid / 2 - eps,
    maxLat: cellLat + grid / 2 + eps,
    minLng: cellLng - grid / 2 - eps,
    maxLng: cellLng + grid / 2 + eps,
  };
}

// ── MeterPopup (from RoutesPage) ─────────────────────────────────────────────
function MeterPopup({ meters, m, r, hasIncident, checked, checkedCount, batchSize }) {
  const [idx, setIdx] = useState(0);
  const mt      = meters[idx] || {};
  const mChecked = mt.checked !== undefined ? mt.checked : m.checked;
  const mValue   = mt.reading_value !== undefined ? mt.reading_value : m.reading_value;
  const mPhoto   = mt.photo !== undefined ? mt.photo : m.photo;

  const row = (label, value, bold) => (
    <div style={{ display: "flex", gap: 4, fontSize: 12, marginBottom: 3 }}>
      <span style={{ color: "#9a9a93", minWidth: 76, flexShrink: 0 }}>{label}</span>
      {bold
        ? <strong style={{ color: "#1a1a18" }}>{value}</strong>
        : <span style={{ color: "#1a1a18" }}>{value}</span>}
    </div>
  );

  return (
    <div style={{ fontFamily: "Montserrat, sans-serif", fontSize: 13, width: 240 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{
          background: hasIncident ? "#fef3c7" : checked ? "#dcfce7" : "#f2f2ef",
          color:      hasIncident ? "#92400e" : checked ? "#16a34a" : "#6b6b66",
          borderRadius: 6, padding: "2px 8px", fontWeight: 700, fontSize: 12,
        }}>
          {hasIncident ? "⚠ Incidencia" : checked ? "✓ Todo medido" : checkedCount > 0 ? `${checkedCount}/${batchSize} medidos` : "○ Pendiente"}
        </span>
        {batchSize > 1 && (
          <span style={{ fontSize: 11, color: "#9a9a93", fontWeight: 600 }}>
            {idx + 1} / {batchSize}
          </span>
        )}
      </div>

      <div style={{ marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ color: mt.has_incident ? "#f59e0b" : mChecked ? "#16a34a" : "#9a9a93", fontSize: 14 }}>
            {mt.has_incident ? "⚠" : mChecked ? "✓" : "○"}
          </span>
          <strong style={{ fontSize: 14 }}>{mt.meter_code || `Medidor ${idx + 1}`}</strong>
        </div>

        {mValue && row("Lectura:", mValue, true)}
        {(mt.reader_name || r.employee_name) && row("Auditor:", mt.reader_name || r.employee_name, false)}
        {mt.read_at && row("Fecha lectura:", (() => { try { return new Date(mt.read_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return mt.read_at; } })(), false)}

        {mPhoto && (
          <img src={mPhoto} alt="foto"
            style={{ width: "100%", borderRadius: 6, margin: "6px 0 4px", cursor: "pointer", maxHeight: 130, objectFit: "cover" }}
            onClick={() => window.open(mPhoto, "_blank")} />
        )}

        {r.created_at && (
          <div style={{ borderTop: "1px solid #f0f0ec", marginTop: 6, paddingTop: 6 }}>
            {row("Fecha ruta:", (() => { try { return new Date(r.created_at).toLocaleDateString("es-AR"); } catch { return r.created_at; } })(), false)}
          </div>
        )}
      </div>

      {batchSize > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid #f0f0ec", paddingTop: 8, marginTop: 2 }}>
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            style={{ background: "none", border: "1px solid #e6e6e2", borderRadius: 6, padding: "3px 10px", cursor: idx === 0 ? "default" : "pointer", color: idx === 0 ? "#c8c8c4" : "#1a1a18", fontSize: 13, fontWeight: 700 }}
          >‹ Ant</button>
          <div style={{ display: "flex", gap: 3 }}>
            {meters.slice(0, Math.min(batchSize, 8)).map((_, i) => (
              <div key={i} onClick={() => setIdx(i)} style={{ width: 6, height: 6, borderRadius: "50%", background: i === idx ? "#D4620D" : "#e6e6e2", cursor: "pointer", transition: "background 0.15s" }} />
            ))}
            {batchSize > 8 && <span style={{ fontSize: 10, color: "#9a9a93" }}>…</span>}
          </div>
          <button
            onClick={() => setIdx((i) => Math.min(batchSize - 1, i + 1))}
            disabled={idx === batchSize - 1}
            style={{ background: "none", border: "1px solid #e6e6e2", borderRadius: 6, padding: "3px 10px", cursor: idx === batchSize - 1 ? "default" : "pointer", color: idx === batchSize - 1 ? "#c8c8c4" : "#1a1a18", fontSize: 13, fontWeight: 700 }}
          >Sig ›</button>
        </div>
      )}
    </div>
  );
}

// ── meterDotIcon (from RoutesPage) ───────────────────────────────────────────
function meterDotIcon(color, size = 12) {
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.28)"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// ── SelectedMetersOverlay (from MapaOptimizadoPage) ──────────────────────────
function SelectedMetersOverlay({ routeSelection, routeMode, onToggle }) {
  const map = useMapEvents({});
  const groupRef    = useRef(null);
  const onToggleRef = useRef(onToggle);

  useEffect(() => { onToggleRef.current = onToggle; });

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = L.layerGroup().addTo(map);
    }
    const group = groupRef.current;
    group.clearLayers();

    if (!routeMode || routeSelection.size === 0) return;

    routeSelection.forEach((m) => {
      const lat = parseFloat(m.latitude);
      const lng = parseFloat(m.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      const circle = L.circleMarker([lat, lng], {
        radius: 5,
        color: "#b45309",
        fillColor: "#D4620D",
        fillOpacity: 0.95,
        weight: 1.5,
        renderer: SELECTED_CANVAS,
      });
      circle.on("click", () => onToggleRef.current(m));
      group.addLayer(circle);
    });

    return () => { group.clearLayers(); };
  }, [routeSelection, routeMode, map]);

  useEffect(() => {
    return () => { groupRef.current?.remove(); groupRef.current = null; };
  }, [map]);

  return null;
}

// ── MapEventsHandler (from MapaOptimizadoPage) ───────────────────────────────
function MapEventsHandler({ onBoundsChange }) {
  const map = useMapEvents({
    moveend: () => onBoundsChange(map),
    zoomend: () => onBoundsChange(map),
  });
  useEffect(() => {
    onBoundsChange(map);
  }, []); // eslint-disable-line
  return null;
}

// ── AreaSelector (from MapaOptimizadoPage) ───────────────────────────────────
function AreaSelector({ active, appliedFilters, onSelected, onFetchStart, onFetchEnd }) {
  const [startPt, setStartPt] = useState(null);
  const [currPt,  setCurrPt]  = useState(null);
  const dragging = useRef(false);

  const map = useMapEvents({
    mousedown: (e) => {
      if (!active || e.originalEvent.ctrlKey) return;
      e.originalEvent.preventDefault();
      map.dragging.disable();
      dragging.current = true;
      setStartPt(e.latlng);
      setCurrPt(e.latlng);
    },
    mousemove: (e) => {
      if (!active || !dragging.current) return;
      setCurrPt(e.latlng);
    },
    mouseup: async (e) => {
      if (!active || !dragging.current) return;
      map.dragging.enable();
      dragging.current = false;

      const s = startPt;
      const c = e.latlng;
      setStartPt(null);
      setCurrPt(null);

      if (!s) return;
      if (Math.abs(s.lat - c.lat) < 0.0002 && Math.abs(s.lng - c.lng) < 0.0002) return;

      const bbox = {
        minLat: Math.min(s.lat, c.lat), maxLat: Math.max(s.lat, c.lat),
        minLng: Math.min(s.lng, c.lng), maxLng: Math.max(s.lng, c.lng),
      };
      onFetchStart();
      try {
        const res = await getReadingsForMap({ ...bbox, limit: 5000, ...appliedFilters });
        const data = Array.isArray(res) ? res : res.data ?? [];
        onSelected(data);
      } catch (err) {
        console.error("Error fetching area meters:", err);
      } finally {
        onFetchEnd();
      }
    },
  });

  useEffect(() => {
    const container = map.getContainer();
    container.style.cursor = active ? "crosshair" : "";
    return () => { container.style.cursor = ""; };
  }, [active, map]);

  if (!active || !startPt || !currPt) return null;

  const bounds = [
    [Math.min(startPt.lat, currPt.lat), Math.min(startPt.lng, currPt.lng)],
    [Math.max(startPt.lat, currPt.lat), Math.max(startPt.lng, currPt.lng)],
  ];

  return (
    <Rectangle
      bounds={bounds}
      pathOptions={{ color: "#16a34a", fillColor: "#16a34a", fillOpacity: 0.12, weight: 2, dashArray: "6 4" }}
    />
  );
}

// ── Segmentation colors ───────────────────────────────────────────────────────
const SEGMENTACION_COLORS = {
  residencial: { fill: "#16a34a", stroke: "#15803d" },
  comercial:   { fill: "#1e3a8a", stroke: "#1e40af" },
  default:     { fill: "#2563eb", stroke: "#1d4ed8" },
};

// ── MeterDot (from MapaOptimizadoPage) ───────────────────────────────────────
function MeterDot({ reading, isEditSelected, isRouteSelected, routeMode, onEditClick, onRouteToggle }) {
  const seg = SEGMENTACION_COLORS[reading.segmentacion] ?? SEGMENTACION_COLORS.default;
  let fillColor   = seg.fill;
  let strokeColor = seg.stroke;
  let radius = 5;

  if (isRouteSelected) {
    fillColor   = "#D4620D";
    strokeColor = "#b45309";
    radius = 7;
  } else if (isEditSelected) {
    fillColor   = "#D4620D";
    strokeColor = "#b45309";
    radius = 6;
  }

  function handleClick(e) {
    if (routeMode) {
      onRouteToggle(reading);
    } else {
      onEditClick(reading);
    }
  }

  return (
    <CircleMarker
      center={[reading.latitude, reading.longitude]}
      radius={radius}
      renderer={CANVAS_RENDERER}
      pathOptions={{ color: strokeColor, fillColor, fillOpacity: 0.9, weight: routeMode ? 1.5 : 1 }}
      eventHandlers={{ click: handleClick }}
    />
  );
}

// ── RoutePanel (from MapaOptimizadoPage) ─────────────────────────────────────
function RoutePanel({ selection, employees, tarifas, onRemove, onClear, onClose, onRouteCreated }) {
  const meters = Array.from(selection.values());
  const [form, setForm] = useState({ name: "", employeeId: "", valor: "", zona: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const selectedEmployee = employees.find((e) => e.id === form.employeeId);
  const precioPorMedidor = parseFloat(tarifas?.precio_por_vehiculo?.[selectedEmployee?.vehicle_type]) || 0;
  const zonaPct = form.zona && tarifas?.zonas_porcentaje?.[form.zona]
    ? parseFloat(tarifas.zonas_porcentaje[form.zona]) : 0;
  const precioFinal = precioPorMedidor * (1 + zonaPct / 100);
  const valorPersonalizado = form.valor ? parseFloat(form.valor) : null;
  const precioEfectivo = valorPersonalizado ?? precioFinal;
  const totalEstimado = Math.round(meters.length * precioEfectivo);

  async function handleSave() {
    if (!form.name.trim()) { setError("El nombre de la ruta es requerido"); return; }
    if (meters.length === 0) { setError("Seleccioná al menos un medidor"); return; }
    setSaving(true);
    setError(null);
    try {
      const lats = meters.map((m) => parseFloat(m.latitude));
      const lngs = meters.map((m) => parseFloat(m.longitude));
      const buf  = 0.0005;
      const polygon = [
        [Math.min(...lats) - buf, Math.min(...lngs) - buf],
        [Math.max(...lats) + buf, Math.min(...lngs) - buf],
        [Math.max(...lats) + buf, Math.max(...lngs) + buf],
        [Math.min(...lats) - buf, Math.max(...lngs) + buf],
      ];
      const payload = {
        name: form.name.trim(),
        polygon,
        meter_ids: meters.map((m) => m.id),
        zona: form.zona || null,
        employee_id: form.employeeId || null,
        valor_personalizado: totalEstimado > 0 ? totalEstimado : null,
      };
      await createRoute(payload);
      setSuccess(true);
      setTimeout(() => {
        onRouteCreated();
      }, 1000);
    } catch (e) {
      setError(e.message || "Error al crear la ruta");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={rp.panel}>
      <div style={rp.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Navigation2 size={16} color="#16a34a" />
          <span style={rp.title}>Crear Ruta</span>
        </div>
        <button style={rp.closeBtn} onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      <div style={rp.instructions}>
        <MousePointerClick size={12} style={{ flexShrink: 0, marginRight: 6 }} />
        <span>
          Hacé <b>clic</b> en puntos para seleccionar. <b>Ctrl+Clic</b> en un cluster
          para seleccionar todos sus medidores.
        </span>
      </div>

      <div style={rp.counter}>
        <span style={rp.counterNum}>{meters.length.toLocaleString()}</span>
        <span style={rp.counterLabel}>medidores seleccionados</span>
        {meters.length > 0 && (
          <button style={rp.clearSelBtn} onClick={onClear}>
            <Trash2 size={12} style={{ marginRight: 4 }} />
            Limpiar
          </button>
        )}
      </div>

      <div style={rp.body}>
        <div style={rp.field}>
          <label style={rp.label}>Nombre de la ruta *</label>
          <input
            style={rp.input}
            placeholder="Ej: Ruta Zona Norte"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div style={rp.field}>
          <label style={rp.label}>Asignar auditor</label>
          <select
            style={rp.input}
            value={form.employeeId}
            onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}
          >
            <option value="">Sin asignar</option>
            {employees
              .filter((e) => e.role === "lector-plus")
              .map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
          </select>
        </div>
        <div style={rp.field}>
          <label style={rp.label}>Zona</label>
          <select
            style={rp.input}
            value={form.zona}
            onChange={(e) => setForm((f) => ({ ...f, zona: e.target.value }))}
          >
            <option value="">Sin zona</option>
            {["1","2","3","4"].map((z) => {
              const pct = tarifas?.zonas_porcentaje?.[z];
              return (
                <option key={z} value={z}>
                  Zona {z}{pct ? ` (+${pct}%)` : ""}
                </option>
              );
            })}
          </select>
        </div>

        <div style={rp.field}>
          <label style={rp.label}>Valor personalizado ($)</label>
          <input
            style={rp.input}
            type="number"
            placeholder={precioPorMedidor ? `Base: $${precioFinal.toLocaleString("es-AR")} por medidor` : "Opcional"}
            value={form.valor}
            onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
          />
        </div>

        {meters.length > 0 && precioEfectivo > 0 && (
          <div style={rp.priceBox}>
            <div style={rp.priceRow}>
              <span style={rp.priceLabel}>
                {meters.length.toLocaleString()} medidores × ${precioEfectivo.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
              </span>
            </div>
            {zonaPct > 0 && !valorPersonalizado && (
              <div style={rp.priceZone}>Incluye {zonaPct}% de zona {form.zona}</div>
            )}
            {valorPersonalizado && (
              <div style={rp.priceZone}>Precio personalizado aplicado</div>
            )}
            <div style={rp.priceTotal}>
              Total estimado: <strong>${totalEstimado.toLocaleString("es-AR")}</strong>
            </div>
          </div>
        )}

        {meters.length > 0 && (
          <div style={rp.field}>
            <label style={rp.label}>Medidores ({meters.length})</label>
            <div style={rp.meterList}>
              {meters.slice(0, 80).map((m) => (
                <div key={m.id} style={rp.meterItem}>
                  <span style={rp.meterCode}>{m.meter_code || "—"}</span>
                  <span style={rp.meterAddr}>{m.address || ""}</span>
                  <button style={rp.removeBtn} onClick={() => onRemove(m.id)}>
                    <X size={10} />
                  </button>
                </div>
              ))}
              {meters.length > 80 && (
                <div style={rp.moreLabel}>+{meters.length - 80} más…</div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <div style={rp.errorMsg}>{error}</div>}
      {success && (
        <div style={rp.successMsg}>
          <CheckCircle2 size={14} style={{ marginRight: 6 }} />
          Ruta creada correctamente
        </div>
      )}
      <div style={rp.footer}>
        <button style={rp.cancelBtn} onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button
          style={{ ...rp.saveBtn, ...(saving || meters.length === 0 ? rp.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving || meters.length === 0}
        >
          <Save size={14} style={{ marginRight: 6 }} />
          {saving ? "Guardando…" : "Crear ruta"}
        </button>
      </div>
    </div>
  );
}

// ── EditPanel (from MapaOptimizadoPage) ──────────────────────────────────────
function EditPanel({ meter, onClose, onSaved }) {
  const [form, setForm] = useState({
    meter_code: meter.meter_code || "",
    address: meter.address || "",
    notes: meter.notes || "",
    latitude: meter.latitude ?? "",
    longitude: meter.longitude ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        meter_code: form.meter_code.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
        latitude: parseFloat(form.latitude) || null,
        longitude: parseFloat(form.longitude) || null,
      };
      await updateReading(meter.id, payload);
      setSuccess(true);
      setTimeout(() => {
        onSaved({ ...meter, ...payload });
        setSuccess(false);
      }, 800);
    } catch (e) {
      setError(e.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  function field(label, key, type = "text") {
    return (
      <div style={ep.field}>
        <label style={ep.label}>{label}</label>
        <input
          type={type}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          style={ep.input}
        />
      </div>
    );
  }

  return (
    <div style={ep.panel}>
      <div style={ep.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Edit2 size={16} color="#D4620D" />
          <span style={ep.title}>Editar Medidor</span>
        </div>
        <button style={ep.closeBtn} onClick={onClose}><X size={16} /></button>
      </div>
      <div style={ep.meta}>
        <span style={ep.metaChip}>ID: {meter.id?.slice(0, 8)}…</span>
        {meter.user_name && <span style={ep.metaChip}>Auditor: {meter.user_name}</span>}
      </div>
      <div style={ep.body}>
        {field("Código de medidor", "meter_code")}
        {field("Dirección", "address")}
        {field("Notas", "notes")}
        {field("Latitud", "latitude", "number")}
        {field("Longitud", "longitude", "number")}
      </div>
      {error   && <div style={ep.errorMsg}>{error}</div>}
      {success && <div style={ep.successMsg}>Guardado correctamente</div>}
      <div style={ep.footer}>
        <button style={ep.cancelBtn} onClick={onClose} disabled={saving}>Cancelar</button>
        <button
          style={{ ...ep.saveBtn, ...(saving ? ep.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving}
        >
          <Save size={14} style={{ marginRight: 6 }} />
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MapaRutasPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";
  const navigate = useNavigate();

  // ── Map state (from MapaOptimizadoPage) ──────────────────────────────────
  const [points, setPoints]           = useState([]);
  const [selected, setSelected]       = useState(null);
  const [loading, setLoading]         = useState(false);
  const [pointCount, setPointCount]   = useState(0);
  const [employees, setEmployees]     = useState([]);
  const [tarifas, setTarifas]         = useState(null);

  // Route creation mode
  const [routeMode, setRouteMode]               = useState(false);
  const [areaSelectMode, setAreaSelectMode]     = useState(false);
  const [routeSelection, setRouteSelection]     = useState(new Map());
  const [selectedClusterKeys, setSelectedClusterKeys] = useState(new Set());
  const [fetchingCluster, setFetchingCluster]   = useState(false);

  // Filters
  const [filters, setFilters] = useState({ userId: "", from: "", to: "", zone: "", meterCode: "", segmentacion: "" });
  const [appliedFilters, setAppliedFilters] = useState({});

  // Map refs
  const mapRef      = useRef(null);
  const boundsRef   = useRef(null);
  const zoomRef     = useRef(13);
  const debounceRef = useRef(null);

  // ── Routes sidebar state (from RoutesPage) ───────────────────────────────
  const [routes, setRoutes]               = useState([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState(new Set());
  const [routeSearch, setRouteSearch]     = useState("");
  const [routesLoading, setRoutesLoading] = useState(true);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Edit modal
  const [showModal, setShowModal]       = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editForm, setEditForm]         = useState({ name: "", employee_id: "", valor_personalizado: "", zona: "" });
  const [keepProgress, setKeepProgress] = useState(false);
  const [saving, setSaving]             = useState(false);
  const [editError, setEditError]       = useState("");

  // History modal
  const [historyRoute, setHistoryRoute]       = useState(null);
  const [historyData, setHistoryData]         = useState([]);
  const [historyLoading, setHistoryLoading]   = useState(false);
  const [historyError, setHistoryError]       = useState("");
  const [completing, setCompleting]           = useState(false);

  // ── Initial data load ────────────────────────────────────────────────────
  useEffect(() => {
    getEmployees()
      .then((e) =>
        setEmployees(
          e.filter((u) => u.active && u.role !== "admin" && u.role !== "admin-plus")
        )
      )
      .catch(() => {});
    getTarifas().then(setTarifas).catch(() => {});
    loadRoutes();
  }, []);

  async function loadRoutes() {
    setRoutesLoading(true);
    try {
      const [routeRes, empRes, tarifasRes] = await Promise.allSettled([
        getRoutes(),
        getEmployees(),
        getTarifas(),
      ]);
      if (routeRes.status === "fulfilled") setRoutes(routeRes.value);
      // employees and tarifas already loaded above; update if also returned here
      if (empRes.status === "fulfilled") {
        setEmployees(empRes.value.filter((u) => u.active && u.role !== "admin" && u.role !== "admin-plus"));
      }
      if (tarifasRes.status === "fulfilled") setTarifas(tarifasRes.value);
    } catch (err) {
      console.error("Error loading routes:", err);
    } finally {
      setRoutesLoading(false);
    }
  }

  // ── Map data fetch ───────────────────────────────────────────────────────
  const fetchData = useCallback(async (bounds, zoom, currentFilters) => {
    if (!bounds) return;
    setLoading(true);
    const bbox = {
      minLat: bounds.getSouth(), maxLat: bounds.getNorth(),
      minLng: bounds.getWest(),  maxLng: bounds.getEast(),
    };
    try {
      const res = await getReadingsForMap({ ...bbox, ...currentFilters, limit: 5000 });
      const data = Array.isArray(res) ? res : res.data ?? [];
      setPoints(data);
      setPointCount(data.length);
    } catch (err) {
      console.error("Error fetching map data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBoundsChange = useCallback((map) => {
    mapRef.current    = map;
    boundsRef.current = map.getBounds();
    zoomRef.current   = map.getZoom();
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(boundsRef.current, zoomRef.current, appliedFilters);
    }, DEBOUNCE_MS);
  }, [fetchData, appliedFilters]);

  useEffect(() => {
    if (boundsRef.current) fetchData(boundsRef.current, zoomRef.current, appliedFilters);
  }, [appliedFilters, fetchData]);

  // ── Filters ──────────────────────────────────────────────────────────────
  function applyFilters() {
    const f = {};
    if (filters.userId)       f.userId       = filters.userId;
    if (filters.from)         f.from         = filters.from;
    if (filters.to)           f.to           = filters.to;
    if (filters.zone)         f.zone         = filters.zone;
    if (filters.meterCode)    f.meterCode    = filters.meterCode;
    if (filters.segmentacion) f.segmentacion = filters.segmentacion;
    setAppliedFilters(f);
  }
  function clearFilters() {
    setFilters({ userId: "", from: "", to: "", zone: "", meterCode: "", segmentacion: "" });
    setAppliedFilters({});
  }

  // ── Edit meter ───────────────────────────────────────────────────────────
  function handleMeterSaved(updated) {
    setPoints((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setSelected(updated);
  }

  // ── Route creation mode ──────────────────────────────────────────────────
  function toggleRouteMode() {
    setRouteMode((prev) => {
      if (prev) {
        setRouteSelection(new Map());
        setSelectedClusterKeys(new Set());
        setAreaSelectMode(false);
      }
      return !prev;
    });
    setSelected(null);
  }

  function toggleRoutePoint(meter) {
    setRouteSelection((prev) => {
      const next = new Map(prev);
      if (next.has(meter.id)) next.delete(meter.id);
      else next.set(meter.id, meter);
      return next;
    });
  }

  function removeFromRoute(id) {
    setRouteSelection((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }

  async function handleClusterCtrlSelect(cluster) {
    setFetchingCluster(true);
    try {
      const bbox = exactClusterBbox(cluster.lat, cluster.lng, zoomRef.current);
      const res = await getReadingsForMap({
        ...bbox,
        limit: 10000,
        ...appliedFilters,
      });
      const data = Array.isArray(res) ? res : res.data ?? [];
      setRouteSelection((prev) => {
        const next = new Map(prev);
        data.forEach((m) => next.set(m.id, m));
        return next;
      });
      setSelectedClusterKeys((prev) => new Set([...prev, `${cluster.lat},${cluster.lng}`]));
    } catch (err) {
      console.error("Error fetching cluster meters:", err);
    } finally {
      setFetchingCluster(false);
    }
  }

  function handleAreaSelected(meters) {
    setRouteSelection((prev) => {
      const next = new Map(prev);
      meters.forEach((m) => next.set(m.id, m));
      return next;
    });
    setAreaSelectMode(false);
  }

  function handleRouteCreated() {
    setRouteMode(false);
    setAreaSelectMode(false);
    setRouteSelection(new Map());
    setSelectedClusterKeys(new Set());
    setSelected(null);
    loadRoutes();
  }

  // ── Routes sidebar handlers ──────────────────────────────────────────────
  function flyToRoute(route) {
    if (!mapRef.current || !route.polygon?.length) return;
    mapRef.current.flyToBounds(route.polygon, {
      padding: [40, 40],
      maxZoom: 17,
      duration: 0.8,
    });
  }

  function openEditModal(route) {
    setEditingRoute(route);
    setEditForm({
      name: route.name,
      employee_id: route.employee_id?.toString() || "",
      valor_personalizado: route.valor_personalizado != null ? String(route.valor_personalizado) : "",
      zona: route.zona || "",
    });
    setKeepProgress(false);
    setEditError("");
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingRoute(null);
    setEditError("");
  }

  async function handleSaveRoute() {
    if (!editForm.name.trim()) {
      setEditError("El nombre es requerido");
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      const valorPers = editForm.valor_personalizado !== "" ? parseFloat(editForm.valor_personalizado) : null;
      const zonaVal = editForm.zona || null;
      const employeeChanged = (editForm.employee_id || null) !== (editingRoute.employee_id?.toString() || null);
      await updateRoute(editingRoute.id, {
        name: editForm.name.trim(),
        employee_id: editForm.employee_id || null,
        valor_personalizado: valorPers,
        zona: zonaVal,
        ...(employeeChanged && keepProgress && { keep_progress: true }),
      });
      closeModal();
      loadRoutes();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function handleDeleteRoute(route) {
    setDeleteTarget(route);
  }

  async function openHistory(route) {
    setHistoryRoute(route);
    setHistoryData([]);
    setHistoryError("");
    setHistoryLoading(true);
    try {
      const data = await getRouteHistory(route.id);
      setHistoryData(data);
    } catch (err) {
      setHistoryError(err.message || "Error al cargar historial");
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleCompleteAssignment() {
    if (!historyRoute) return;
    setCompleting(true);
    setHistoryError("");
    try {
      await completeRouteAssignment(historyRoute.id);
      await loadRoutes();
      const data = await getRouteHistory(historyRoute.id);
      setHistoryData(data);
      setHistoryRoute((r) => ({ ...r, employee_id: null, employee_name: null }));
    } catch (err) {
      setHistoryError(err.message || "Error al completar la asignación");
    } finally {
      setCompleting(false);
    }
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const hasFilters      = Object.keys(appliedFilters).length > 0;
  const selectionMeters = Array.from(routeSelection.values());
  const panelOpen       = routeMode || (selected && !routeMode);

  // Compute selected meter IDs from selected routes for map filter
  const selectedMeterIds = selectedRouteIds.size > 0
    ? new Set(
        routes
          .filter((r) => selectedRouteIds.has(r.id))
          .flatMap((r) => (r.meter_codes || []))
          .flatMap((mc) => (mc.meters || []).map((mt) => mt.id))
      )
    : null;

  // When routes are selected, filter map points to those meters
  const displayedPoints = selectedMeterIds
    ? points.filter((p) => selectedMeterIds.has(p.id))
    : points;

  const activeEmployees = employees.filter((e) => e.active);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <DeleteConfirmModal
        visible={!!deleteTarget}
        message={`¿Eliminar la ruta "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await deleteRoute(deleteTarget.id);
          setDeleteTarget(null);
          loadRoutes();
        }}
      />

      {/* ── Top bar ── */}
      <div style={s.topBar}>
        <div style={s.titleRow}>
          <MapPin size={20} color="#D4620D" />
          <h1 style={s.title}>Rutas y Mapa</h1>
          {(loading || fetchingCluster) && <RefreshCw size={15} style={s.spinner} />}
          <div style={s.statusChips}>
            <span style={s.chip}>
              <Layers size={12} style={{ marginRight: 4 }} />
              {`${pointCount.toLocaleString()} puntos`}
            </span>
            {hasFilters && (
              <span style={{ ...s.chip, background: "#FBF0E8", color: "#D4620D" }}>
                Filtros activos
              </span>
            )}
            {routeMode && (
              <span style={{ ...s.chip, background: "#dcfce7", color: "#15803d" }}>
                <Navigation2 size={11} style={{ marginRight: 4 }} />
                Modo Ruta — {selectionMeters.length} seleccionados
              </span>
            )}
          </div>

          <button
            style={{ ...s.routeBtn, ...(routeMode ? s.routeBtnActive : {}) }}
            onClick={toggleRouteMode}
          >
            <Navigation2 size={14} style={{ marginRight: 6 }} />
            {routeMode ? "Cancelar ruta" : "Crear ruta"}
          </button>

          {routeMode && (
            <button
              style={{ ...s.areaBtn, ...(areaSelectMode ? s.areaBtnActive : {}) }}
              onClick={() => setAreaSelectMode((p) => !p)}
              title="Arrastrá para seleccionar un área"
            >
              <RectangleHorizontal size={14} style={{ marginRight: 6 }} />
              {areaSelectMode ? "Cancelar área" : "Seleccionar área"}
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={s.filters}>
          <select
            style={s.select}
            value={filters.userId}
            onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
          >
            <option value="">Todos los auditores</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <input type="date" style={s.filterInput} value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
          <input type="date" style={s.filterInput} value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
          <select style={s.select} value={filters.zone}
            onChange={(e) => setFilters((f) => ({ ...f, zone: e.target.value }))}
          >
            <option value="">Todas las zonas</option>
            {["1","2","3","4"].map((z) => <option key={z} value={z}>Zona {z}</option>)}
          </select>
          <select
            style={s.select}
            value={filters.segmentacion}
            onChange={(e) => setFilters((f) => ({ ...f, segmentacion: e.target.value }))}
          >
            <option value="">Todas las segmentaciones</option>
            <option value="residencial">Residencial</option>
            <option value="comercial">Comercial</option>
          </select>
          <input
            style={s.filterInput}
            placeholder="Código medidor…"
            value={filters.meterCode}
            onChange={(e) => setFilters((f) => ({ ...f, meterCode: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
          />
          <button style={s.searchBtn} onClick={applyFilters}>
            <Search size={14} style={{ marginRight: 6 }} />Buscar
          </button>
          {hasFilters && (
            <button style={s.clearBtn} onClick={clearFilters}><X size={14} /></button>
          )}
        </div>

        <div style={s.hint}>
          <Info size={12} style={{ marginRight: 4, flexShrink: 0 }} />
          {routeMode
            ? areaSelectMode
              ? "Área: arrastrá sobre el mapa para seleccionar todos los medidores del rectángulo"
              : "Clic en punto · Ctrl+Clic en cluster · Botón 'Seleccionar área' para arrastrar un rectángulo"
            : selectedRouteIds.size > 0
              ? "Mostrando medidores de las rutas seleccionadas · Clic en punto para editar"
              : "Seleccioná rutas del panel derecho para filtrar el mapa · Clic en punto para editar"}
        </div>
      </div>

      {/* ── Main layout: map + panels + sidebar ── */}
      <div style={s.mainRow}>
        {/* Map area */}
        <div style={{
          ...s.mapArea,
          width: panelOpen ? "calc(100% - 340px - 30% - 12px)" : "calc(100% - 30%)",
        }}>
          {/* Route chip overlay on map */}
          {selectedRouteIds.size > 0 && (
            <div style={s.mapChip}>
              {selectedRouteIds.size === 1
                ? routes.find((r) => selectedRouteIds.has(r.id))?.name
                : `${selectedRouteIds.size} rutas seleccionadas`}
              <button
                onClick={() => setSelectedRouteIds(new Set())}
                style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
                title="Limpiar selección"
              >
                <X size={14} />
              </button>
            </div>
          )}

          <MapContainer
            center={[-34.6, -58.45]}
            zoom={12}
            style={{ width: "100%", height: "100%" }}
            preferCanvas={true}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              maxZoom={19}
            />
            <MapEventsHandler onBoundsChange={handleBoundsChange} />

            <AreaSelector
              active={routeMode && areaSelectMode}
              appliedFilters={appliedFilters}
              onSelected={handleAreaSelected}
              onFetchStart={() => setFetchingCluster(true)}
              onFetchEnd={() => setFetchingCluster(false)}
            />


            {/* Individual meter dots — todos por defecto, filtrados si hay rutas seleccionadas */}
            {displayedPoints.map((r) => (
              <MeterDot
                key={r.id}
                reading={r}
                isEditSelected={!routeMode && selected?.id === r.id}
                isRouteSelected={routeSelection.has(r.id)}
                routeMode={routeMode}
                onEditClick={setSelected}
                onRouteToggle={toggleRoutePoint}
              />
            ))}

            {/* Route markers with popup (from RoutesPage) — when routes selected */}
            {!routeMode && routes.filter((r) => selectedRouteIds.has(r.id)).map((r, ri) => {
              const routeColor = ROUTE_COLORS[routes.indexOf(r) % ROUTE_COLORS.length];
              return (r.meter_codes || []).map((m, mi) => {
                const lat = parseFloat(m.latitude);
                const lng = parseFloat(m.longitude);
                if (isNaN(lat) || isNaN(lng)) return null;
                const meters = m.meters || [];
                const batchSize = meters.length || 1;
                const checked = !!m.checked;
                const hasIncident = meters.some((mt) => !!mt.has_incident);
                const checkedCount = meters.filter((mt) =>
                  mt.checked !== undefined ? mt.checked : m.checked
                ).length;
                const color = hasIncident
                  ? "#f59e0b"
                  : checked
                    ? "#16a34a"
                    : routeColor;
                const size = Math.min(20, 12 + (batchSize - 1) * 2);
                return (
                  <Marker
                    key={`${r.id}-${mi}`}
                    position={[lat, lng]}
                    icon={meterDotIcon(color, size)}
                  >
                    <Popup minWidth={240} maxWidth={260}>
                      <MeterPopup
                        meters={meters}
                        m={m}
                        r={r}
                        hasIncident={hasIncident}
                        checked={checked}
                        checkedCount={checkedCount}
                        batchSize={batchSize}
                      />
                    </Popup>
                  </Marker>
                );
              });
            })}

            {/* Polygons ocultos */}
            {false && routes
              .filter((r) => !selectedRouteIds.has(r.id) && r.polygon?.length >= 3)
              .map((r, i) => (
                <Polygon
                  key={`poly-${r.id}`}
                  positions={r.polygon}
                  pathOptions={{
                    color: ROUTE_COLORS[routes.indexOf(r) % ROUTE_COLORS.length],
                    fillOpacity: 0.06,
                    weight: 1.5,
                    dashArray: "4 4",
                  }}
                  eventHandlers={{
                    click: () => {
                      setSelectedRouteIds((prev) => {
                        const next = new Set(prev);
                        next.add(r.id);
                        return next;
                      });
                      flyToRoute(r);
                    },
                  }}
                />
              ))}

            {/* Selected meters overlay for route creation */}
            <SelectedMetersOverlay
              routeSelection={routeSelection}
              routeMode={routeMode}
              onToggle={toggleRoutePoint}
            />
          </MapContainer>
        </div>

        {/* Route creation panel */}
        {routeMode && (
          <RoutePanel
            selection={routeSelection}
            employees={employees}
            tarifas={tarifas}
            onRemove={removeFromRoute}
            onClear={() => { setRouteSelection(new Map()); setSelectedClusterKeys(new Set()); }}
            onClose={toggleRouteMode}
            onRouteCreated={handleRouteCreated}
          />
        )}

        {/* Edit panel (normal mode, point selected) */}
        {!routeMode && selected && (
          <EditPanel
            key={selected.id}
            meter={selected}
            onClose={() => setSelected(null)}
            onSaved={handleMeterSaved}
          />
        )}

        {/* Routes sidebar */}
        <div style={s.sidebar}>
          <div style={s.sidebarHeader}>
            <span style={s.sidebarTitle}>
              {routes.length} ruta{routes.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div style={s.sidebarSearch}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              style={s.sidebarSearchInput}
              placeholder="Buscar ruta..."
              value={routeSearch}
              onChange={(e) => setRouteSearch(e.target.value)}
            />
            {routeSearch && (
              <button style={s.sidebarSearchClear} onClick={() => setRouteSearch("")}>✕</button>
            )}
          </div>

          <div style={s.sidebarBody}>
            {routesLoading ? (
              <div style={s.sidebarEmpty}>Cargando...</div>
            ) : routes.length === 0 ? (
              <div style={s.sidebarEmpty}>
                <MapPin size={32} color="#d1d5db" style={{ marginBottom: 10 }} />
                <p style={{ color: "var(--text-2)", margin: 0, fontSize: 13 }}>
                  No hay rutas aún.
                  <br />
                  Creá una con el botón "Crear ruta".
                </p>
              </div>
            ) : (() => {
              const filtered = routeSearch.trim()
                ? routes.filter((r) => r.name?.toLowerCase().includes(routeSearch.toLowerCase()))
                : routes;
              if (filtered.length === 0) return (
                <div style={s.sidebarEmpty}>
                  <p style={{ color: "var(--text-2)", margin: 0, fontSize: 13 }}>Sin resultados para "{routeSearch}"</p>
                </div>
              );
              return filtered.map((r, i) => {
                const globalIdx = routes.indexOf(r);
                return (
                  <div
                    key={r.id}
                    style={{
                      ...s.routeCard,
                      cursor: "pointer",
                      ...(selectedRouteIds.has(r.id) ? {
                        borderLeft: "3px solid #D4620D",
                        background: "var(--primary-bg)",
                      } : {}),
                    }}
                    onClick={() => {
                      const isSelected = selectedRouteIds.has(r.id);
                      setSelectedRouteIds((prev) => {
                        const next = new Set(prev);
                        if (isSelected) next.delete(r.id);
                        else next.add(r.id);
                        return next;
                      });
                      if (!isSelected) flyToRoute(r);
                    }}
                  >
                    <div style={s.routeCardTop}>
                      <div style={s.routeCardLeft}>
                        <span
                          style={{
                            ...s.colorDot,
                            background: ROUTE_COLORS[globalIdx % ROUTE_COLORS.length],
                          }}
                        />
                        <span style={s.routeName}>{r.name}</span>
                      </div>
                      <div
                        style={s.routeCardActions}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          style={s.iconBtnGray}
                          title="Historial"
                          onClick={() => openHistory(r)}
                        >
                          <Clock size={12} />
                        </button>
                        {isAdminPlus && (
                          <>
                            <button
                              style={s.iconBtnBlue}
                              title="Editar"
                              onClick={() => openEditModal(r)}
                            >
                              <Edit2 size={12} />
                            </button>
                            <button
                              style={s.iconBtnRed}
                              title="Eliminar"
                              onClick={() => handleDeleteRoute(r)}
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {(() => {
                      const totalMeters = (r.meter_codes || []).reduce(
                        (sum, mc) => sum + (mc.meters?.length || 0), 0
                      );
                      const precioPorMedidor = parseFloat(tarifas?.precio_por_vehiculo?.[r.employee_vehicle_type]) || 0;
                      const zonaPct = r.zona && tarifas?.zonas_porcentaje?.[r.zona]
                        ? parseFloat(tarifas.zonas_porcentaje[r.zona]) : 0;
                      const precioFinal = precioPorMedidor * (1 + zonaPct / 100);
                      const valorRef = Math.round(totalMeters * precioFinal);
                      const valorPers = r.valor_personalizado != null ? parseFloat(r.valor_personalizado) : null;

                      const today = new Date().toISOString().split("T")[0];
                      const obj = r.objetivo_completado;
                      const objHoy = obj && obj.fecha === today ? obj : null;

                      return (
                        <div style={s.routeMeta}>
                          <div style={s.routeMetaRow}>
                            <User
                              size={11}
                              color={r.employee_name ? "#D4620D" : "#d8d8d3"}
                            />
                            <span style={r.employee_name ? s.empName : s.empEmpty}>
                              {r.employee_name || "Sin asignar"}
                            </span>
                            {r.zona && (
                              <span style={s.zonaBadge}>Zona {r.zona}{zonaPct > 0 ? ` +${zonaPct}%` : ""}</span>
                            )}
                          </div>
                          <div style={s.routeMetaRow}>
                            <span style={s.routeDate}>
                              {totalMeters} medidor{totalMeters !== 1 ? "es" : ""}
                              {precioPorMedidor > 0 && (
                                <> · Ref: <strong style={{ color: "var(--text-2)" }}>${valorRef.toLocaleString("es-AR")}</strong></>
                              )}
                            </span>
                          </div>
                          {valorPers != null && (
                            <div style={s.routeMetaRow}>
                              <span style={{ ...s.routeDate, color: "#D4620D", fontWeight: 700 }}>
                                Valor personalizado: ${valorPers.toLocaleString("es-AR")}
                              </span>
                            </div>
                          )}
                          {objHoy && objHoy.nivel_actual && (
                            <div style={s.routeMetaRow}>
                              <span style={s.objetivoBadge}>
                                Objetivo {objHoy.nivel_actual.medidores} medidores
                                {" — "}${parseFloat(objHoy.nivel_actual.premio).toLocaleString("es-AR")} extra
                              </span>
                            </div>
                          )}
                          {objHoy && !objHoy.nivel_actual && objHoy.medidores_hoy > 0 && (
                            <div style={s.routeMetaRow}>
                              <span style={{ ...s.routeDate, color: "var(--text-2)" }}>
                                Hoy: {objHoy.medidores_hoy} medidor{objHoy.medidores_hoy !== 1 ? "es" : ""}
                              </span>
                            </div>
                          )}
                          <div style={s.routeMetaRow}>
                            <span style={s.routeDate}>
                              {r.created_at
                                ? format(new Date(r.created_at), "d MMM yyyy", { locale: es })
                                : "—"}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* ── Edit Route Modal ── */}
      {isAdminPlus && showModal && (
        <div
          style={m.overlay}
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div style={m.box}>
            <div style={m.header}>
              <h3 style={m.title}>
                {editingRoute ? "Editar ruta" : "Nueva ruta"}
              </h3>
              <button style={m.closeBtn} onClick={closeModal}>
                <X size={16} />
              </button>
            </div>

            {editingRoute && (
              <div style={m.editPreview}>
                <span
                  style={{
                    ...s.colorDot,
                    background:
                      ROUTE_COLORS[
                        routes.findIndex((r) => r.id === editingRoute.id) %
                          ROUTE_COLORS.length
                      ],
                  }}
                />
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
                  {editingRoute.name}
                </span>
              </div>
            )}

            {!editingRoute && (
              <div style={m.field}>
                <label style={m.label}>Nombre de la ruta *</label>
                <input
                  style={m.input}
                  autoFocus
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Ej: Zona Norte Bloque A"
                  onKeyDown={(e) => e.key === "Enter" && handleSaveRoute()}
                />
              </div>
            )}

            <div style={m.field}>
              <label style={m.label}>Zona</label>
              <select
                style={m.input}
                value={editForm.zona}
                onChange={(e) => setEditForm((f) => ({ ...f, zona: e.target.value }))}
              >
                <option value="">Sin zona</option>
                {["1", "2", "3", "4"].map((z) => {
                  const pct = parseFloat(tarifas?.zonas_porcentaje?.[z]) || 0;
                  return (
                    <option key={z} value={z}>
                      Zona {z}{pct > 0 ? ` (+${pct}%)` : ""}
                    </option>
                  );
                })}
              </select>
            </div>

            {editingRoute && (
              <div style={m.field}>
                <label style={m.label}>Asignar operario</label>
                <select
                  style={m.input}
                  value={editForm.employee_id}
                  onChange={(e) => {
                    setEditForm((f) => ({ ...f, employee_id: e.target.value }));
                    setKeepProgress(false);
                  }}
                >
                  <option value="">Sin asignar</option>
                  {activeEmployees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                      {e.role === "lector-plus" ? " ★" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {editingRoute && (() => {
              const newEmp = editForm.employee_id || null;
              const oldEmp = editingRoute.employee_id?.toString() || null;
              const changing = newEmp && newEmp !== oldEmp;
              const hasProgress = (editingRoute.meter_codes || []).some((mc) =>
                (mc.meters || []).some((mt) => mt.checked)
              );
              if (!changing || !hasProgress) return null;
              return (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={keepProgress}
                      onChange={(e) => setKeepProgress(e.target.checked)}
                      style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer", accentColor: "#D4620D" }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
                        Mantener medidores ya registrados
                      </div>
                      <div style={{ fontSize: 12, color: "#b45309", marginTop: 2, lineHeight: 1.4 }}>
                        El nuevo auditor solo verá los medidores pendientes. Los ya medidos quedan como están.
                        Si no lo marcás, se borrará todo el progreso anterior.
                      </div>
                    </div>
                  </label>
                </div>
              );
            })()}

            {editingRoute && (() => {
              const totalMeters = (editingRoute.meter_codes || []).reduce(
                (sum, mc) => sum + (mc.meters?.length || 0), 0
              );
              const empVehicle = editingRoute?.employee_vehicle_type
                || employees.find((e) => e.id === editingRoute?.employee_id)?.vehicle_type;
              const precioPorMedidor = parseFloat(tarifas?.precio_por_vehiculo?.[empVehicle]) || 0;
              if (!precioPorMedidor) return null;
              const zonaPct = editForm.zona && tarifas?.zonas_porcentaje?.[editForm.zona]
                ? parseFloat(tarifas.zonas_porcentaje[editForm.zona]) : 0;
              const precioFinal = precioPorMedidor * (1 + zonaPct / 100);
              const valorRef = Math.round(totalMeters * precioFinal);
              return (
                <div style={m.refBox}>
                  <span style={m.refLabel}>Valor de referencia</span>
                  <span style={m.refValue}>
                    {totalMeters} medidores × ${precioFinal.toLocaleString("es-AR")}
                    {zonaPct > 0 && <span style={{ color: "#92400e" }}> (base +{zonaPct}% Zona {editForm.zona})</span>}
                    {" = "}<strong>${valorRef.toLocaleString("es-AR")}</strong>
                  </span>
                </div>
              );
            })()}

            <div style={m.field}>
              <label style={m.label}>Valor personalizado de la ruta (opcional)</label>
              <div style={m.inputPrefixWrap}>
                <span style={m.inputPrefixSign}>$</span>
                <input
                  style={{ ...m.input, borderRadius: "0 8px 8px 0", border: "none", background: "transparent" }}
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.valor_personalizado}
                  onChange={(e) => setEditForm((f) => ({ ...f, valor_personalizado: e.target.value }))}
                  placeholder="Dejar vacío para usar valor de referencia"
                />
              </div>
            </div>

            {editError && <p style={m.error}>{editError}</p>}

            <div style={m.actions}>
              <button style={m.btnCancel} onClick={closeModal}>
                Cancelar
              </button>
              <button style={m.btnSave} onClick={handleSaveRoute} disabled={saving}>
                {saving
                  ? "Guardando..."
                  : editingRoute
                    ? "Guardar cambios"
                    : "Crear ruta"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── History Modal ── */}
      {historyRoute && (
        <div
          style={m.overlay}
          onClick={(e) => e.target === e.currentTarget && setHistoryRoute(null)}
        >
          <div style={{ ...m.box, maxWidth: 520 }}>
            <div style={m.header}>
              <div>
                <h3 style={m.title}>Historial de asignaciones</h3>
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-2)" }}>
                  {historyRoute.name}
                </p>
              </div>
              <button style={m.closeBtn} onClick={() => setHistoryRoute(null)}>
                <X size={16} />
              </button>
            </div>

            {historyRoute.employee_name && isAdminPlus && (
              <div style={h.activeBox}>
                <div style={h.activeLeft}>
                  <CheckCircle size={15} color="#16a34a" style={{ flexShrink: 0 }} />
                  <div>
                    <div style={h.activeName}>{historyRoute.employee_name}</div>
                    <div style={h.activeLabel}>Asignación activa</div>
                  </div>
                </div>
                <button
                  style={{ ...h.btnComplete, ...(completing ? h.btnCompleteDisabled : {}) }}
                  onClick={handleCompleteAssignment}
                  disabled={completing}
                  title="Marcar como completada y liberar la ruta"
                >
                  {completing ? "..." : "Completar"}
                </button>
              </div>
            )}

            {historyError && (
              <div style={h.errorBanner}>{historyError}</div>
            )}

            <div style={h.listWrap}>
              {historyLoading ? (
                <div style={h.empty}>Cargando historial...</div>
              ) : historyData.length === 0 ? (
                <div style={h.empty}>
                  <Clock size={28} color="#d1d5db" style={{ marginBottom: 8 }} />
                  <p style={{ margin: 0, color: "var(--text-2)", fontSize: 13 }}>
                    No hay asignaciones anteriores
                  </p>
                </div>
              ) : (
                historyData.map((a) => (
                  <div key={a.id} style={h.item}>
                    <div style={h.itemLeft}>
                      <div style={h.avatar}>
                        {a.employee_name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div style={h.itemName}>{a.employee_name}</div>
                        <div style={h.itemEmail}>{a.employee_email}</div>
                        <div style={h.itemDates}>
                          {format(new Date(a.assigned_at), "d MMM yyyy", { locale: es })}
                          {" → "}
                          {a.completed_at
                            ? format(new Date(a.completed_at), "d MMM yyyy", { locale: es })
                            : "hoy"}
                        </div>
                        {a.notas && (
                          <div style={h.itemNotas}>"{a.notas}"</div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                      <span style={{
                        ...h.statusBadge,
                        ...(a.status === "activa" ? h.statusActiva
                          : a.status === "completada" ? h.statusCompletada
                          : h.statusCancelada),
                      }}>
                        {a.status}
                      </span>
                      {a.status === "completada" && (
                        <button
                          style={h.verRutaBtn}
                          onClick={() => {
                            setHistoryRoute(null);
                            navigate("/gestion-ruta", { state: { openAssignmentId: a.id } });
                          }}
                        >
                          Ver ruta →
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page styles ───────────────────────────────────────────────────────────────
const s = {
  page: { display: "flex", flexDirection: "column", height: "calc(100vh - 64px)" },
  topBar: {
    background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: 14, padding: "14px 18px", marginBottom: 12, flexShrink: 0,
  },
  titleRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" },
  title: { fontSize: 18, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.4px", margin: 0 },
  spinner: { color: "#D4620D", animation: "spin 1s linear infinite" },
  statusChips: { display: "flex", gap: 8, flex: 1, flexWrap: "wrap" },
  chip: {
    display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 700,
    background: "var(--bg-subtle)", color: "var(--text-2)", borderRadius: 100, padding: "3px 10px",
  },
  routeBtn: {
    display: "inline-flex", alignItems: "center", height: 34, padding: "0 16px",
    borderRadius: 8, border: "2px solid #16a34a", background: "transparent",
    color: "#16a34a", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif", transition: "all 0.15s",
  },
  routeBtnActive: {
    background: "#16a34a", color: "#fff",
  },
  areaBtn: {
    display: "inline-flex", alignItems: "center", height: 34, padding: "0 14px",
    borderRadius: 8, border: "2px solid #0891b2", background: "transparent",
    color: "#0891b2", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif", transition: "all 0.15s",
  },
  areaBtnActive: {
    background: "#0891b2", color: "#fff",
  },
  filters: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 },
  select: {
    height: 34, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif", cursor: "pointer", minWidth: 150,
  },
  filterInput: {
    height: 34, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
  },
  searchBtn: {
    display: "inline-flex", alignItems: "center", height: 34, padding: "0 16px",
    borderRadius: 8, border: "none", background: "#D4620D", color: "#fff",
    fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  clearBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    height: 34, width: 34, borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
  },
  hint: { display: "flex", alignItems: "center", fontSize: 11, color: "var(--text-3)" },

  // Main layout row
  mainRow: { flex: 1, display: "flex", gap: 12, minHeight: 0 },

  // Map area
  mapArea: {
    position: "relative",
    borderRadius: 14, overflow: "hidden", border: "1px solid var(--border)",
    transition: "width 0.2s ease", minHeight: 0, flex: "1 1 auto",
  },
  mapChip: {
    position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
    zIndex: 1000, background: "#D4620D", color: "#fff", borderRadius: 20,
    padding: "6px 16px", fontSize: 13, fontWeight: 700,
    display: "flex", alignItems: "center", gap: 8, boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    whiteSpace: "nowrap",
  },

  // Routes sidebar
  sidebar: {
    flex: "0 0 30%",
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid var(--border)",
    height: "90vh",
    overflow: "hidden",
    background: "var(--bg-card)",
    borderRadius: 14,
    border: "1px solid var(--border)",
  },
  sidebarSearch: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderBottom: "1px solid var(--border)",
    background: "var(--bg-card)",
    flexShrink: 0,
  },
  sidebarSearchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    fontSize: 13,
    color: "var(--text)",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
  },
  sidebarSearchClear: {
    background: "none",
    border: "none",
    color: "var(--text-3)",
    cursor: "pointer",
    fontSize: 12,
    padding: 2,
    lineHeight: 1,
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 18px",
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
  },
  sidebarTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text-2)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  sidebarBody: { flex: 1, overflowY: "auto" },
  sidebarEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    padding: 24,
  },

  // Route cards
  routeCard: { padding: "14px 18px", borderBottom: "1px solid var(--border)" },
  routeCardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  routeCardLeft: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  routeCardActions: { display: "flex", gap: 5, flexShrink: 0 },
  colorDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  routeName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  routeMeta: {
    marginLeft: 18,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  routeMetaRow: { display: "flex", alignItems: "center", gap: 5 },
  empName: { fontSize: 13, color: "#D4620D", fontWeight: 700 },
  empEmpty: { fontSize: 13, color: "var(--text-2)", fontStyle: "italic" },
  routeDate: { fontSize: 13, color: "var(--text-2)" },
  zonaBadge: {
    fontSize: 10,
    fontWeight: 700,
    background: "var(--warning-bg)",
    color: "var(--warning-text)",
    borderRadius: 5,
    padding: "2px 6px",
    marginLeft: 4,
    flexShrink: 0,
  },
  objetivoBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: "#7c3aed",
    background: "#ede9fe",
    borderRadius: 6,
    padding: "3px 8px",
  },
  iconBtnGray: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: 7,
    border: "none",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    cursor: "pointer",
  },
  iconBtnBlue: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: 7,
    border: "none",
    background: "var(--primary-bg)",
    color: "#D4620D",
    cursor: "pointer",
  },
  iconBtnRed: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    borderRadius: 7,
    border: "none",
    background: "var(--error-bg)",
    color: "var(--error-text)",
    cursor: "pointer",
  },
};

// ── Route panel styles ────────────────────────────────────────────────────────
const rp = {
  panel: {
    width: 328, flexShrink: 0, background: "var(--bg-card)",
    border: "1px solid var(--border)", borderRadius: 14,
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0,
  },
  title: { fontSize: 14, fontWeight: 800, color: "var(--text)" },
  closeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
  },
  instructions: {
    display: "flex", alignItems: "flex-start", fontSize: 11, color: "var(--text-2)",
    padding: "10px 16px", borderBottom: "1px solid var(--border)",
    background: "#f0fdf4", flexShrink: 0,
  },
  counter: {
    display: "flex", alignItems: "center", gap: 8, padding: "12px 16px",
    borderBottom: "1px solid var(--border)", flexShrink: 0,
  },
  counterNum: { fontSize: 22, fontWeight: 800, color: "#16a34a" },
  counterLabel: { fontSize: 12, color: "var(--text-2)", flex: 1 },
  clearSelBtn: {
    display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 700,
    padding: "4px 10px", borderRadius: 7, border: "1px solid #fca5a5",
    background: "#fef2f2", color: "#dc2626", cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  body: { flex: 1, overflowY: "auto", padding: "14px 16px 8px", display: "flex", flexDirection: "column", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.4px" },
  input: {
    padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif", outline: "none", width: "100%", boxSizing: "border-box",
  },
  meterList: {
    maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)",
    borderRadius: 8, background: "var(--bg)",
  },
  meterItem: {
    display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
    borderBottom: "1px solid var(--border)", fontSize: 12,
  },
  meterCode: { fontWeight: 700, color: "var(--text)", minWidth: 70 },
  meterAddr: { flex: 1, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  removeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, borderRadius: 4, border: "none",
    background: "var(--bg-subtle)", color: "var(--text-3)", cursor: "pointer", flexShrink: 0,
  },
  moreLabel: { padding: "6px 10px", fontSize: 11, color: "var(--text-3)", fontStyle: "italic" },
  priceBox: {
    background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10,
    padding: "10px 14px", display: "flex", flexDirection: "column", gap: 4,
  },
  priceRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  priceLabel: { fontSize: 12, color: "#15803d" },
  priceZone: { fontSize: 11, color: "#16a34a", opacity: 0.8 },
  priceTotal: { fontSize: 14, fontWeight: 800, color: "#15803d", marginTop: 2 },
  errorMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 12, fontWeight: 600 },
  successMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#f0fdf4", color: "#16a34a", borderRadius: 8, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center" },
  footer: { display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--border)", flexShrink: 0 },
  cancelBtn: {
    flex: 1, padding: "9px 0", borderRadius: 9, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  saveBtn: {
    flex: 2, display: "flex", alignItems: "center", justifyContent: "center",
    padding: "9px 0", borderRadius: 9, border: "none", background: "#16a34a",
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  saveBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};

// ── Edit panel styles ─────────────────────────────────────────────────────────
const ep = {
  panel: {
    width: 328, flexShrink: 0, background: "var(--bg-card)",
    border: "1px solid var(--border)", borderRadius: 14,
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0,
  },
  title: { fontSize: 14, fontWeight: 800, color: "var(--text)" },
  closeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
  },
  meta: { display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 },
  metaChip: { fontSize: 10, fontWeight: 700, background: "var(--bg-subtle)", color: "var(--text-2)", borderRadius: 100, padding: "2px 8px" },
  body: { flex: 1, overflowY: "auto", padding: "16px 16px 8px", display: "flex", flexDirection: "column", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.4px" },
  input: {
    padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif", outline: "none", width: "100%", boxSizing: "border-box",
  },
  errorMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 12, fontWeight: 600 },
  successMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#f0fdf4", color: "#16a34a", borderRadius: 8, fontSize: 12, fontWeight: 600 },
  footer: { display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--border)", flexShrink: 0 },
  cancelBtn: {
    flex: 1, padding: "9px 0", borderRadius: 9, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  saveBtn: {
    flex: 2, display: "flex", alignItems: "center", justifyContent: "center",
    padding: "9px 0", borderRadius: 9, border: "none", background: "#D4620D",
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  saveBtnDisabled: { opacity: 0.6, cursor: "not-allowed" },
};

// ── Modal styles ──────────────────────────────────────────────────────────────
const m = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    padding: 24,
  },
  box: {
    background: "var(--bg-card)",
    borderRadius: 16,
    padding: 28,
    width: "100%",
    maxWidth: 460,
    boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
    border: "1px solid var(--border)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 22,
    paddingBottom: 16,
    borderBottom: "1px solid var(--border)",
  },
  title: { fontSize: 17, fontWeight: 800, color: "var(--text)", margin: 0 },
  closeBtn: {
    background: "var(--bg-subtle)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    cursor: "pointer",
    color: "var(--text-2)",
    display: "flex",
    padding: 6,
  },
  editPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    padding: "10px 14px",
    background: "var(--bg-subtle)",
    borderRadius: 10,
    border: "1px solid var(--border)",
  },
  field: { marginBottom: 16 },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "var(--text-2)",
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  input: {
    width: "100%",
    padding: "10px 13px",
    borderRadius: 10,
    border: "1.5px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    fontSize: 14,
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "Montserrat, sans-serif",
    transition: "border-color 0.15s",
  },
  polygonOk: {
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 700,
    color: "var(--success-text)",
    background: "var(--success-bg)",
    border: "1px solid var(--success-border)",
    padding: "9px 13px",
    borderRadius: 10,
  },
  polygonNone: {
    marginBottom: 16,
    fontSize: 13,
    color: "var(--text-2)",
    background: "var(--bg-subtle)",
    border: "1px solid var(--border)",
    padding: "9px 13px",
    borderRadius: 10,
  },
  refBox: {
    background: "var(--success-bg)",
    border: "1px solid var(--success-border)",
    borderRadius: 12,
    padding: "10px 14px",
    marginBottom: 16,
  },
  refLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "var(--success-text)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 4,
  },
  refValue: { fontSize: 13, color: "var(--text)" },
  inputPrefixWrap: {
    display: "flex",
    alignItems: "stretch",
    borderRadius: 10,
    border: "1.5px solid var(--border)",
    overflow: "hidden",
    background: "var(--bg)",
  },
  inputPrefixSign: {
    padding: "10px 12px",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    fontSize: 14,
    fontWeight: 700,
    borderRight: "1.5px solid var(--border)",
    display: "flex",
    alignItems: "center",
  },
  error: { color: "var(--error-text)", fontSize: 13, margin: "0 0 12px" },
  actions: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 8,
    paddingTop: 16,
    borderTop: "1px solid var(--border)",
  },
  btnCancel: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-subtle)",
    color: "var(--text-2)",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
    fontFamily: "Montserrat, sans-serif",
  },
  btnSave: {
    padding: "10px 22px",
    borderRadius: 10,
    border: "none",
    background: "#D4620D",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14,
    fontFamily: "Montserrat, sans-serif",
  },
};

// ── History modal styles ───────────────────────────────────────────────────────
const h = {
  activeBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "var(--success-bg)",
    border: "1px solid var(--success-border)",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 16,
    gap: 10,
  },
  activeLeft: { display: "flex", alignItems: "center", gap: 10 },
  activeName: { fontSize: 14, fontWeight: 700, color: "var(--text)" },
  activeLabel: { fontSize: 11, color: "var(--success-text)", fontWeight: 700, textTransform: "uppercase" },
  btnComplete: {
    padding: "6px 14px",
    borderRadius: 10,
    border: "none",
    background: "#16a34a",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "Montserrat, sans-serif",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  btnCompleteDisabled: { opacity: 0.6, cursor: "not-allowed" },
  errorBanner: {
    background: "var(--error-bg)",
    color: "var(--error-text)",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 12,
  },
  listWrap: {
    maxHeight: 380,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "32px 0",
    color: "var(--text-2)",
    fontSize: 13,
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "12px 4px",
    borderBottom: "1px solid var(--border)",
    gap: 10,
  },
  itemLeft: { display: "flex", alignItems: "flex-start", gap: 12 },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "var(--info-bg)",
    color: "var(--info-text)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0,
  },
  itemName: { fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 2 },
  itemEmail: { fontSize: 11, color: "var(--text-2)", marginBottom: 3 },
  itemDates: { fontSize: 12, color: "var(--text-2)" },
  itemNotas: { fontSize: 12, color: "var(--text-2)", fontStyle: "italic", marginTop: 3 },
  statusBadge: {
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 6,
    padding: "3px 8px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    flexShrink: 0,
    alignSelf: "center",
  },
  statusActiva: { background: "var(--success-bg)", color: "var(--success-text)" },
  statusCompletada: { background: "var(--bg-subtle)", color: "var(--text-2)" },
  statusCancelada: { background: "var(--error-bg)", color: "var(--error-text)" },
  verRutaBtn: {
    background: "none",
    border: "1px solid var(--border)",
    borderRadius: 7,
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 700,
    color: "#D4620D",
    cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    whiteSpace: "nowrap",
  },
};
